import { useCallback, useMemo, useState } from 'react';
import { DropdownProps, MenuProps } from 'antd';
import { ToolbarButtonProps } from './ToolbarButton';
import {
  $getSelection,
  $isElementNode,
  ElementFormatType,
  LexicalEditor,
} from 'lexical';

import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { ToolbarDropdownButton, renderMenuItem } from './ToolbarDropdownButton';
import { Icon } from '@/components/Icon';
import { useTranslation } from 'react-i18next';

export type IndentType = 'indent' | 'outdent';

export interface ToolbarAlignDropdownButtonProps
  extends ToolbarButtonProps,
    Pick<DropdownProps, 'trigger' | 'overlay'> {
  selected?: ElementFormatType | IndentType;
}

const ELEMENT_FORMAT_TYPES: ElementFormatType[] = [
  'left',
  'start',
  'center',
  'right',
  'end',
  'justify',
];

export const ALIGN_META_BASE: Record<
  ElementFormatType | IndentType,
  { icon: React.ReactNode }
> = {
  left: { icon: <AlignLeftOutlined /> },
  center: { icon: <AlignCenterOutlined /> },
  right: { icon: <AlignRightOutlined /> },
  justify: { icon: <Icon type="justify" /> },
  indent: { icon: <ArrowRightOutlined /> },
  outdent: { icon: <ArrowLeftOutlined /> },
  '': { icon: undefined },
  start: { icon: undefined },
  end: { icon: undefined },
};

function useLocalizedAlignMeta() {
  const { t } = useTranslation();

  return useMemo(() => {
    const entries = Object.entries(ALIGN_META_BASE);

    return Object.fromEntries(
      entries.map(([key, val]) => [
        key,
        {
          icon: val.icon,
          label: t(`editor.align.${key}`),
        },
      ]),
    ) as Record<
      ElementFormatType | IndentType,
      { icon: React.ReactNode; label: string }
    >;
  }, [t]);
}

export function useAlignTypeMenu(editor: LexicalEditor) {
  const [selected, setSelected] = useState<IndentType | ElementFormatType>(
    'left',
  );
  const meta = useLocalizedAlignMeta();
  const { t } = useTranslation();

  const selectType = useCallback(
    (type: IndentType | ElementFormatType) => {
      setSelected(type);

      editor.update(() => {
        const selection = $getSelection();
        if (!selection) return;

        const nodes = selection.getNodes();

        nodes.forEach((node) => {
          const element = node.getTopLevelElementOrThrow();
          const writable = element.getWritable();

          if (
            ELEMENT_FORMAT_TYPES.includes(type as ElementFormatType) &&
            $isElementNode(writable)
          ) {
            writable.setFormat(type as ElementFormatType);
            return;
          }

          if (type === 'indent' && $isElementNode(writable)) {
            const current = Math.min(writable.getIndent() ?? 0, 20);
            writable.setIndent(current + 2);
            return;
          }

          if (type === 'outdent' && $isElementNode(writable)) {
            const current = writable.getIndent() ?? 0;
            writable.setIndent(Math.max(0, current - 2));
            return;
          }
        });
      });
    },
    [editor],
  );

  const items = useMemo<MenuProps['items']>(() => {
    return [
      {
        type: 'group',
        label: t('editor.alignGroup.alignment'),
        children: [
          {
            key: 'left',
            label: renderMenuItem(meta.left.icon, meta.left.label),
            onClick: () => selectType('left'),
          },
          {
            key: 'center',
            label: renderMenuItem(meta.center.icon, meta.center.label),
            onClick: () => selectType('center'),
          },
          {
            key: 'right',
            label: renderMenuItem(meta.right.icon, meta.right.label),
            onClick: () => selectType('right'),
          },
          {
            key: 'justify',
            label: renderMenuItem(meta.justify.icon, meta.justify.label),
            onClick: () => selectType('justify'),
          },
        ],
      },

      {
        type: 'group',
        label: t('editor.alignGroup.indent'),
        children: [
          {
            key: 'indent',
            label: renderMenuItem(meta.indent.icon, meta.indent.label),
            onClick: () => selectType('indent'),
          },
          {
            key: 'outdent',
            label: renderMenuItem(meta.outdent.icon, meta.outdent.label),
            onClick: () => selectType('outdent'),
          },
        ],
      },
    ];
  }, [meta, selectType, t]);

  return { blockTypeMenu: items, selected };
}

export function ToolbarAlignDropdownButton(
  props: ToolbarAlignDropdownButtonProps,
) {
  const { selected, ...dropDownProps } = props;
  const meta = useLocalizedAlignMeta();
  const blockMeta = selected ? meta[selected] : undefined;

  return <ToolbarDropdownButton {...dropDownProps} selectedItem={blockMeta} />;
}
